function getDBKSessionInfo() {
    const { sessionInfo } = window.dbk || {};

    return sessionInfo?.();
}

/** Default telephone country code when WHATSAPP_SMS payload omits it (US). */
export const DEFAULT_WHATSAPP_TELEPHONE_COUNTRY_CODE = '1';

/**
 * Normalize country code for WHATSAPP_SMS (and matching against VOICE/SMS rows).
 * Empty / null / undefined → {@link DEFAULT_WHATSAPP_TELEPHONE_COUNTRY_CODE}.
 *
 * @param {string|number|null|undefined} countryCode
 * @returns {string}
 */
export function normalizeWhatsAppTelephoneCountryCode(countryCode) {
    if (countryCode === undefined || countryCode === null) {
        return DEFAULT_WHATSAPP_TELEPHONE_COUNTRY_CODE;
    }

    const trimmed = String(countryCode).trim();

    if (trimmed === '') {
        return DEFAULT_WHATSAPP_TELEPHONE_COUNTRY_CODE;
    }

    return trimmed;
}

/**
 * Identity string for duplicate detection / pairing (normalized CC + contact).
 *
 * @param {string} contactInfo
 * @param {string|number|undefined} telephoneCountryCode
 * @returns {string}
 */
export function whatsAppPhoneIdentity(contactInfo, telephoneCountryCode) {
    const info =
        contactInfo === undefined || contactInfo === null
            ? ''
            : String(contactInfo);

    return normalizeWhatsAppTelephoneCountryCode(telephoneCountryCode) + info;
}

/**
 * True when a VOICE row and a WHATSAPP_SMS row refer to the same phone (same contact, same normalized country).
 *
 * @param {{ contactInfo: string, telephoneCountryCode?: string|number }} voiceDest
 * @param {{ contactInfo: string, telephoneCountryCode?: string|number }} waDest
 * @returns {boolean}
 */
export function voiceDestinationMatchesWhatsAppSms(voiceDest, waDest) {
    if (!voiceDest || !waDest) {
        return false;
    }

    const voiceContact =
        voiceDest.contactInfo === undefined || voiceDest.contactInfo === null
            ? ''
            : String(voiceDest.contactInfo).trim();
    const waContact =
        waDest.contactInfo === undefined || waDest.contactInfo === null
            ? ''
            : String(waDest.contactInfo).trim();

    if (voiceContact === '' || waContact === '') {
        return false;
    }

    if (voiceContact !== waContact) {
        return false;
    }

    return (
        String(
            normalizeWhatsAppTelephoneCountryCode(
                voiceDest.telephoneCountryCode,
            ),
        ) ===
        String(
            normalizeWhatsAppTelephoneCountryCode(waDest.telephoneCountryCode),
        )
    );
}

/**
 * Security overview should only show “WhatsApp enabled” when the destination is activated server-side.
 *
 * @param {{ activated?: boolean|string }} [dest]
 * @returns {boolean}
 */
export function isWhatsAppSmsActivatedForPresentation(dest) {
    if (!dest) {
        return false;
    }

    return dest.activated === true || dest.activated === 'true';
}

/**
 * Central feature switch for WhatsApp destinations (security settings UI + related client behavior).
 *
 * @param {Object} [config] - Page/config object (e.g. security options / confirm-id config).
 * @returns {boolean}
 */
export function isWhatsAppDestinationsEnabled(config) {
    const { isMobileFlow } = getDBKSessionInfo() || {};

    if (isMobileFlow) {
        return config?.isWhatsAppEnabledMobile ?? false;
    }

    return config?.isWhatsAppEnabled ?? false;
}
